// This Hook should be used by all modal cancel buttons to close the modal
// with "Escape" key.
// This "replaces" the keydown event with a "click" event on the cancel button.
// This way, we insure that "escape" is treated the same way as a click does.

// Caveats:
// * If any element within the modal may trigger a nested modal, then this nested
// modal *must* have a cancel button with this hook, otherwise the "Escape" key
// will wrongly close the parent modal.
import { isVisible } from "../helpers"

class ModalCancelManager {
  constructor() {
    this.allCancelButtons = []
    document.addEventListener("keydown", (event) => this.keydown(event))
  }
  keydown(event) {
    // Ignore anything else than the "Escape" key:
    if (event.key !== "Escape") return
    // While `dialog` naturally handles the "Escape" key, it doesn't
    // it doesn't prevent the event from bubbling up to the document.
    // So we need to check if the event was triggered on a dialog element before handling it.
    if (this.triggeredOnDialogElement(event)) return
    // A control inside the modal already handled this Escape: a menu closing
    // itself, or the comment editor leaving reply mode.
    if (event.defaultPrevented) return
    // An open popover takes Escape first; the browser closes it.
    if (this.popoverOpen()) return

    // We rely on the hooks adding the event listeners in the right order,
    // so look backwards for the first visible cancel button.
    const cancelButton = this.allCancelButtons.findLast((cancelButton) =>
      // In case a modal is rendered but not visible, we don't want to target it.
      isVisible(cancelButton.el),
    )
    if (cancelButton) this.trigger(cancelButton, event)
  }
  register(cancelButton) {
    this.allCancelButtons.push(cancelButton)
  }
  unregister(cancelButton) {
    this.allCancelButtons = this.allCancelButtons.filter(
      (cb) => cb.el.id !== cancelButton.el.id,
    )
  }
  popoverOpen() {
    try {
      return !!document.querySelector(':popover-open:not([popover="manual"])')
    } catch {
      // Browsers without the Popover API reject the selector.
      return false
    }
  }
  triggeredOnDialogElement(event) {
    return !!event.target.closest("dialog")
  }
  trigger(cancelButton, event) {
    event.stopImmediatePropagation()

    // I'm not sure I understand why, but if the click triggers `Confirm.confirm`,
    // e.g. because there's an `unload-verify` attribute on the element,
    // then there's a "close" event that immediately dismisses the `<dialog>`.
    // So we cheat and wait for the next event cycle to do the actual click
    setTimeout(() => cancelButton.el.click())
  }
}
export const modalCancelManager = new ModalCancelManager()

export const ModalCancel = {
  mounted() {
    modalCancelManager.register(this)
  },
  destroyed() {
    modalCancelManager.unregister(this)
  },
}
